/**
 * DV-STOR-001: Artifact Verification Utility
 *
 * Utilities for verifying URLs, detecting CORS issues, and handling errors
 */

import type { ArtifactVerification, VerificationLogEntry } from '@/types/stage-outputs';

export class ArtifactVerificationError extends Error {
  constructor(
    message: string,
    public url: string,
    public status?: number,
    public statusText?: string,
    public hint?: string
  ) {
    super(message);
    this.name = 'ArtifactVerificationError';
  }
}

/**
 * Verify a URL using HEAD→GET fallback with robust CORS and mixed-content detection
 */
export async function verifyArtifactUrl(
  url: string,
  options: {
    timeout?: number;
    preferHead?: boolean;
    followRedirects?: boolean;
    signal?: AbortSignal;
  } = {}
): Promise<ArtifactVerification> {
  const { timeout = 10000, followRedirects = true, signal } = options;
  const startTime = Date.now();

  const result: ArtifactVerification = {
    url,
    status: 'pending',
    lastChecked: new Date(),
  };

  // Pre-check for mixed content
  if (typeof window !== 'undefined' && window.location.protocol === 'https:' && url.startsWith('http:')) {
    result.status = 'mixed-content';
    result.error = 'Mixed content (HTTP resource on HTTPS page)';
    result.responseTime = Date.now() - startTime;
    return result;
  }

  try {
    // Create timeout controller, chained with external signal
    const timeoutController = new AbortController();
    const timeoutId = setTimeout(() => timeoutController.abort(), timeout);

    // Chain external signal if provided
    const combinedSignal = signal ? AbortSignal.any([signal, timeoutController.signal]) : timeoutController.signal;

    // Step 1: Try HEAD first (efficient)
    let response: Response;
    let usedMethod = 'HEAD';
    let headFailed = false;

    try {
      response = await fetch(url, {
        method: 'HEAD',
        signal: combinedSignal,
        redirect: followRedirects ? 'follow' : 'manual',
        mode: 'cors',
        cache: 'no-cache',
        headers: {
          'Accept': '*/*',
          // Removed User-Agent - browsers block this header
        },
      });

      // Step 2: If HEAD fails with 405 or other method errors, try GET
      if (!response.ok && (response.status === 405 || response.status === 501)) {
        headFailed = true;
        usedMethod = 'GET (HEAD failed)';
        response = await fetch(url, {
          method: 'GET',
          signal: combinedSignal,
          redirect: followRedirects ? 'follow' : 'manual',
          mode: 'cors',
          cache: 'no-cache',
          headers: {
            'Accept': '*/*',
          },
        });
      }
    } catch (fetchError) {
      // Step 3: If CORS mode fails, probe with no-cors to detect CORS issues
      if (fetchError instanceof TypeError) {
        try {
          const noCorsResponse = await fetch(url, {
            method: 'GET',
            signal: combinedSignal,
            mode: 'no-cors',
            cache: 'no-cache',
          });

          // If no-cors succeeds (opaque response), it's a CORS issue
          if (noCorsResponse.type === 'opaque') {
            result.status = 'cors-error';
            result.error = 'CORS policy blocked the request';
            result.responseTime = Date.now() - startTime;
            // Store metadata about the flow classification
            result.headers = {
              'x-verification-method': 'no-cors',
              'x-verification-flow': 'cors-detection',
              'x-response-type': 'opaque'
            };
            clearTimeout(timeoutId);
            return result;
          }
        } catch (noCorsError) {
          // If no-cors also fails, it's likely a network error
          // Treat 0ms/0 status as network error as specified in ticket
          result.status = 'network-error';
          result.error = 'Network error: Unable to reach resource';
          result.responseTime = Date.now() - startTime;
          result.headers = {
            'x-verification-method': 'failed',
            'x-verification-flow': 'network-error',
            'x-response-time': result.responseTime.toString()
          };
          clearTimeout(timeoutId);
          return result;
        }
      }

      // Re-throw the original error for standard handling
      throw fetchError;
    }

    clearTimeout(timeoutId);

    result.httpStatus = response.status;
    result.statusText = response.statusText;
    result.responseTime = Date.now() - startTime;
    result.corsSupported = response.type !== 'opaque';

    // Extract useful headers and add flow classification metadata
    result.headers = {};
    try {
      // Extract standard headers only on successful responses
      if (response.ok) {
        response.headers.forEach((value, key) => {
          if (['content-type', 'content-length', 'cache-control', 'last-modified'].includes(key.toLowerCase())) {
            result.headers![key] = value;
          }
        });
      }

      // Always add flow classification metadata
      result.headers['x-verification-method'] = usedMethod;
      result.headers['x-verification-flow'] = headFailed ? 'head-then-get' : 'head-only';
      result.headers['x-response-type'] = response.type;
      result.headers['x-response-time'] = result.responseTime!.toString();

      // Add status classification
      if (response.status === 0) {
        result.headers['x-status-classification'] = 'network-error';
      } else if (response.status >= 200 && response.status < 300) {
        result.headers['x-status-classification'] = 'success';
      } else if (response.status >= 400 && response.status < 500) {
        result.headers['x-status-classification'] = 'client-error';
      } else if (response.status >= 500) {
        result.headers['x-status-classification'] = 'server-error';
      }
    } catch (headerError) {
      // Some responses may not allow header iteration
      console.warn('Could not extract headers:', headerError);
      // Ensure flow metadata is always present
      result.headers = {
        'x-verification-method': usedMethod,
        'x-verification-flow': headFailed ? 'head-then-get' : 'head-only',
        'x-response-type': response.type,
        'x-response-time': result.responseTime!.toString()
      };
    }

    // Treat 0 status as network error per ticket specification
    if (response.status === 0 || (result.responseTime === 0 && !response.ok)) {
      result.status = 'network-error';
      result.error = 'Network error: 0 status or 0ms response time';
    } else if (response.ok) {
      result.status = 'success';
    } else {
      result.status = 'failed';
      result.error = `HTTP ${response.status}: ${response.statusText}`;
    }

    return result;

  } catch (error) {
    result.responseTime = Date.now() - startTime;

    if (error instanceof Error) {
      // Handle specific error types
      if (error.name === 'AbortError') {
        result.status = 'network-error';
        result.error = signal?.aborted ? 'Request cancelled' : 'Request timeout';
      } else if (error instanceof TypeError) {
        // Most CORS and network errors are TypeError in fetch
        result.status = 'network-error';
        result.error = 'Network error or resource not found';
      } else {
        result.status = 'failed';
        result.error = error.message;
      }
    } else {
      result.status = 'failed';
      result.error = 'Unknown error occurred';
    }

    return result;
  }
}

/**
 * Per-domain throttling to prevent overwhelming servers
 */
class DomainThrottle {
  private lastRequests = new Map<string, number>();
  private readonly minDelay = 100; // 100ms between requests per domain

  async throttle(url: string): Promise<void> {
    let hostname: string;
    try {
      hostname = new URL(url).hostname;
    } catch {
      return; // Invalid URL, no throttling needed
    }

    const lastRequest = this.lastRequests.get(hostname) || 0;
    const elapsed = Date.now() - lastRequest;

    if (elapsed < this.minDelay) {
      const delay = this.minDelay - elapsed;
      await new Promise(resolve => setTimeout(resolve, delay));
    }

    this.lastRequests.set(hostname, Date.now());
  }

  reset() {
    this.lastRequests.clear();
  }
}

/**
 * Enhanced batch verification with throttling, deduplication, and robust abort support
 */
export async function verifyMultipleArtifacts(
  urls: string[],
  onProgress?: (completed: number, total: number, current: string) => void,
  options?: {
    concurrency?: number;
    timeout?: number;
    signal?: AbortSignal;
    maxUrls?: number;
    enableThrottling?: boolean;
  }
): Promise<Record<string, ArtifactVerification>> {
  const {
    concurrency = 3,
    timeout = 10000,
    signal,
    maxUrls = 100,
    enableThrottling = true
  } = options || {};

  // Deduplicate URLs while preserving order
  const uniqueUrls = Array.from(new Set(urls));
  const results: Record<string, ArtifactVerification> = {};
  const throttle = enableThrottling ? new DomainThrottle() : null;

  // Safety check - prompt for large batches in real usage
  if (uniqueUrls.length > maxUrls) {
    console.warn(`Large batch verification requested (${uniqueUrls.length} URLs). Consider splitting into smaller batches.`);
  }

  let completed = 0;
  const total = uniqueUrls.length;

  // Early exit if already aborted
  if (signal?.aborted) {
    throw new Error('Verification cancelled before starting');
  }

  try {
    // Process URLs in batches to avoid overwhelming the browser
    for (let i = 0; i < uniqueUrls.length; i += concurrency) {
      // Check for abort before each batch
      if (signal?.aborted) {
        throw new Error('Verification cancelled');
      }

      const batch = uniqueUrls.slice(i, i + concurrency);

      const batchPromises = batch.map(async (url) => {
        onProgress?.(completed, total, url);

        try {
          // Apply domain throttling if enabled
          if (throttle) {
            await throttle.throttle(url);
          }

          // Check for abort after throttling delay
          if (signal?.aborted) {
            throw new Error('Verification cancelled');
          }

          const result = await verifyArtifactUrl(url, { timeout, signal });
          results[url] = result;
          return result;
        } catch (error) {
          // Handle individual URL errors gracefully
          if (error instanceof Error && error.message.includes('cancelled')) {
            // Don't log cancelled requests as errors
            const cancelledResult: ArtifactVerification = {
              url,
              status: 'network-error',
              lastChecked: new Date(),
              error: 'Request cancelled',
              responseTime: 0,
            };
            results[url] = cancelledResult;
            return cancelledResult;
          }

          const errorResult: ArtifactVerification = {
            url,
            status: 'failed',
            lastChecked: new Date(),
            error: error instanceof Error ? error.message : 'Unknown error',
            responseTime: 0,
          };
          results[url] = errorResult;
          return errorResult;
        } finally {
          completed++;
          onProgress?.(completed, total, url);
        }
      });

      await Promise.all(batchPromises);
    }

    return results;

  } catch (error) {
    // If cancelled, return partial results
    if (error instanceof Error && error.message.includes('cancelled')) {
      console.log(`Verification cancelled after ${completed}/${total} URLs`);
      // Clean up throttle state
      throttle?.reset();
      return results;
    }
    throw error;
  }
}

/**
 * Generate remediation hints based on verification result
 */
export function getRemediationHint(verification: ArtifactVerification): string {
  switch (verification.status) {
    case 'cors-error':
      return 'CORS policy is blocking access. Check server CORS configuration or use a proxy.';

    case 'mixed-content':
      return 'Mixed content error. Serve resources over HTTPS or update page to HTTP.';

    case 'network-error':
      return 'Network error. Check URL validity and network connectivity.';

    case 'failed':
      if (verification.httpStatus === 403) {
        return 'Access forbidden. Check storage bucket public access policies.';
      } else if (verification.httpStatus === 404) {
        return 'Resource not found. Verify the URL and file existence.';
      } else if (verification.httpStatus === 500) {
        return 'Server error. Check storage service health and configuration.';
      }
      return 'Request failed. Check URL and server configuration.';

    case 'success':
      return 'Resource accessible and working correctly.';

    default:
      return 'Unknown status. Check logs for more details.';
  }
}

/**
 * Create structured log entry for verification result
 */
export function createVerificationLog(
  artifactName: string,
  verification: ArtifactVerification
): VerificationLogEntry {
  const logLevel = verification.status === 'success' ? 'info' :
                  verification.status === 'cors-error' || verification.status === 'mixed-content' ? 'warn' : 'error';

  return {
    id: `verify-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
    ts: new Date().toISOString(),
    level: logLevel,
    artifact: artifactName,
    url: verification.url,
    status: verification.httpStatus || (
      verification.status === 'cors-error' ? 'CORS' :
      verification.status === 'network-error' ? 'NETWORK' :
      verification.status === 'mixed-content' ? 'MIXED_CONTENT' :
      verification.status.toUpperCase()
    ) as number | 'CORS' | 'NETWORK' | 'MIXED_CONTENT',
    responseTime: verification.responseTime || 0,
    error: verification.error,
    hint: getRemediationHint(verification),
    meta: {
      statusText: verification.statusText,
      corsSupported: verification.corsSupported,
      headers: verification.headers,
    },
  };
}

/**
 * Group verification results by hostname for diagnostic summary
 */
export interface HostDiagnostics {
  hostname: string;
  total: number;
  success: number;
  corsError: number;
  mixedContent: number;
  forbidden: number;
  notFound: number;
  networkError: number;
  other: number;
  urls: string[];
}

export function groupVerificationsByHost(
  verifications: Record<string, ArtifactVerification>
): HostDiagnostics[] {
  const hostMap = new Map<string, HostDiagnostics>();

  Object.entries(verifications).forEach(([url, verification]) => {
    let hostname: string;
    try {
      hostname = new URL(url).hostname;
    } catch {
      hostname = 'invalid-url';
    }

    if (!hostMap.has(hostname)) {
      hostMap.set(hostname, {
        hostname,
        total: 0,
        success: 0,
        corsError: 0,
        mixedContent: 0,
        forbidden: 0,
        notFound: 0,
        networkError: 0,
        other: 0,
        urls: [],
      });
    }

    const hostStats = hostMap.get(hostname)!;
    hostStats.total++;
    hostStats.urls.push(url);

    // Categorize by status
    switch (verification.status) {
      case 'success':
        hostStats.success++;
        break;
      case 'cors-error':
        hostStats.corsError++;
        break;
      case 'mixed-content':
        hostStats.mixedContent++;
        break;
      case 'failed':
        if (verification.httpStatus === 403) {
          hostStats.forbidden++;
        } else if (verification.httpStatus === 404) {
          hostStats.notFound++;
        } else {
          hostStats.other++;
        }
        break;
      case 'network-error':
        hostStats.networkError++;
        break;
      default:
        hostStats.other++;
    }
  });

  // Sort by total count descending
  return Array.from(hostMap.values()).sort((a, b) => b.total - a.total);
}

/**
 * Generate Copy cURL command for debugging a URL
 */
export function generateCurlCommand(url: string, options?: { method?: string }): string {
  const { method = 'HEAD' } = options || {};

  const curlParts = [
    'curl',
    '-X', method.toUpperCase(),
    '-H "Accept: */*"',
    '-H "Cache-Control: no-cache"',
    '--max-time 10',
    '--location', // Follow redirects
    '--write-out "%{http_code} %{time_total}s"',
    '--silent',
    '--output /dev/null',
    `"${url}"`
  ];

  return curlParts.join(' ');
}